/**
 * Zustand store for radiation exposure tracking
 * Current flight dose rate, cumulative dose, log entries, threshold settings
 */

import { create } from 'zustand';
import type {
  ExposureLogEntry,
  ExposureThresholdSettings,
  CrewRole,
} from '../types';
import {
  DEFAULT_DOSE_RATE_ALERT_MICRO_SV_PER_HOUR,
  DEFAULT_ANNUAL_CUMULATIVE_ALERT_MICRO_SV,
  DEFAULT_CRUISE_ALTITUDE_FEET,
} from '../utils/constants';
import { CALIBRATION_FACTOR_MICRO_SV_PER_HIT } from '../services/cameraRadiationService';

interface ExposureStore {
  // Current flight
  currentFlightDoseRateMicroSvPerHour: number;

  // Camera radiation: altitude for dose scaling (from GPS or manual)
  cameraAltitudeFeet: number;

  // Live monitoring: hits/sec from camera (for display)
  cameraHitsPerSecond: number;

  // Calibration: µSv/h per hit/sec (user-adjustable for flight altitude)
  calibrationFactorMicroSvPerHit: number;

  // Crew metadata for exportable reports
  defaultFlightId: string;
  defaultCrewRole: CrewRole | '';

  // Cumulative
  totalCumulativeDoseMicroSv: number;

  // Log entries (timestamp, route, dose)
  logEntries: ExposureLogEntry[];

  // Threshold settings
  thresholdSettings: ExposureThresholdSettings;

  // Health app sync (Apple Health / Health Connect)
  healthSyncEnabled: boolean;

  // Test mode: simulate high-energy hits for filter testing (no source needed)
  simulateHighEnergyHits: boolean;

  // Theme: 'dark' | 'light' | 'system' (null = system)
  colorSchemeOverride: 'dark' | 'light' | null;

  // Actions
  setCurrentFlightDoseRate: (rate: number) => void;
  setCameraAltitudeFeet: (altitudeFeet: number) => void;
  setCameraHitsPerSecond: (hits: number) => void;
  setCalibrationFactorMicroSvPerHit: (factor: number) => void;
  setDefaultFlightId: (id: string) => void;
  setDefaultCrewRole: (role: CrewRole | '') => void;
  addLogEntry: (
    entry: Omit<ExposureLogEntry, 'id'>,
    overrides?: { flightId?: string; crewRole?: CrewRole }
  ) => void;
  setTotalCumulativeDose: (dose: number) => void;
  setThresholdSettings: (settings: Partial<ExposureThresholdSettings>) => void;
  setHealthSyncEnabled: (enabled: boolean) => void;
  setSimulateHighEnergyHits: (enabled: boolean) => void;
  setColorSchemeOverride: (scheme: 'dark' | 'light' | null) => void;
  getAnnualCumulativeMicroSv: () => number;
  isDoseRateAboveThreshold: () => boolean;
  isAnnualCumulativeAboveThreshold: () => boolean;
}

const defaultThresholds: ExposureThresholdSettings = {
  doseRateAlertMicroSvPerHour: DEFAULT_DOSE_RATE_ALERT_MICRO_SV_PER_HOUR,
  annualCumulativeAlertMicroSv: DEFAULT_ANNUAL_CUMULATIVE_ALERT_MICRO_SV,
};

const currentYear = new Date().getFullYear();

export const useExposureStore = create<ExposureStore>((set, get) => ({
  currentFlightDoseRateMicroSvPerHour: 0,
  cameraAltitudeFeet: DEFAULT_CRUISE_ALTITUDE_FEET,
  cameraHitsPerSecond: 0,
  calibrationFactorMicroSvPerHit: CALIBRATION_FACTOR_MICRO_SV_PER_HIT,
  defaultFlightId: '',
  defaultCrewRole: '',
  totalCumulativeDoseMicroSv: 0,
  logEntries: [],
  thresholdSettings: defaultThresholds,
  healthSyncEnabled: false,
  simulateHighEnergyHits: false,
  colorSchemeOverride: null,

  setCurrentFlightDoseRate: (rate) =>
    set({ currentFlightDoseRateMicroSvPerHour: rate }),

  setCameraAltitudeFeet: (altitudeFeet) =>
    set({ cameraAltitudeFeet: altitudeFeet }),

  setCameraHitsPerSecond: (hits) => set({ cameraHitsPerSecond: hits }),

  setCalibrationFactorMicroSvPerHit: (factor) =>
    set({ calibrationFactorMicroSvPerHit: factor }),

  setDefaultFlightId: (id: string) => set({ defaultFlightId: id }),
  setDefaultCrewRole: (role: CrewRole | '') => set({ defaultCrewRole: role }),

  addLogEntry: (entry, overrides) =>
    set((s) => {
      const flightId =
        overrides?.flightId ?? entry.flightId ?? (s.defaultFlightId || undefined);
      const crewRole =
        (overrides?.crewRole ?? entry.crewRole ?? (s.defaultCrewRole || undefined)) as
          | CrewRole
          | undefined;
      const fullEntry = {
        ...entry,
        flightId: flightId || undefined,
        crewRole: crewRole || undefined,
        id: `exp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      };
      return {
        logEntries: [...s.logEntries, fullEntry],
        totalCumulativeDoseMicroSv: s.totalCumulativeDoseMicroSv + entry.doseMicroSv,
      };
    }),

  setTotalCumulativeDose: (dose) => set({ totalCumulativeDoseMicroSv: dose }),

  setThresholdSettings: (settings) =>
    set((s) => ({
      thresholdSettings: { ...s.thresholdSettings, ...settings },
    })),

  setHealthSyncEnabled: (enabled) => set({ healthSyncEnabled: enabled }),

  setSimulateHighEnergyHits: (enabled) =>
    set({ simulateHighEnergyHits: enabled }),

  setColorSchemeOverride: (scheme) =>
    set({ colorSchemeOverride: scheme }),

  getAnnualCumulativeMicroSv: () =>
    get().logEntries
      .filter((e) => new Date(e.timestamp).getFullYear() === currentYear)
      .reduce((sum, e) => sum + e.doseMicroSv, 0),

  isDoseRateAboveThreshold: () =>
    get().currentFlightDoseRateMicroSvPerHour >=
    get().thresholdSettings.doseRateAlertMicroSvPerHour,

  isAnnualCumulativeAboveThreshold: () =>
    get().getAnnualCumulativeMicroSv() >=
    get().thresholdSettings.annualCumulativeAlertMicroSv,
}));
